// src/services/asociacionesService.js
import { apiClient } from './api';

/**
 * Servicio para manejar asociaciones contables
 */
export const asociacionesService = {
  /**
   * Obtener documentos pendientes de asociación
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo en formato YYYYMM
   * @returns {Promise<Array>} - Promesa con la lista de documentos pendientes
   */
  async getDocumentosPendientes(rutEmpresa, periodo) {
    return apiClient.get(`/rcv/asociaciones/pendientes?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
  },

  /**
   * Obtener asociaciones de un periodo
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo en formato YYYYMM
   * @returns {Promise<Array>} - Promesa con la lista de asociaciones
   */
  async getAsociaciones(rutEmpresa, periodo) {
    return apiClient.get(`/rcv/asociaciones?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
  },

/**
 * Obtener sugerencia para asociar un documento
 * @param {string} rutEmpresa - RUT de la empresa
 * @param {string} rutProveedor - RUT del proveedor (puede incluir guión y DV)
 * @param {string} folio - Folio del documento
 * @returns {Promise<Object>} - Promesa con la sugerencia o null si no hay sugerencia
 */
async getSugerencia(rutEmpresa, rutProveedor, folio) {
  try {
    // Extraer solo el número del RUT (sin guión ni DV)
    const rutLimpio = rutProveedor.split('-')[0];
    
    const respuesta = await apiClient.get(
      `/rcv/asociaciones/sugerencia?rut_empresa=${rutEmpresa}&rut_proveedor=${rutLimpio}&folio=${folio}`
    );
    
    return respuesta;
  } catch (err) {
    console.warn("No se encontraron sugerencias:", err.message);
    // Retornamos un objeto vacío en caso de error
    return {
      rut_empresa: rutEmpresa,
      rut_proveedor: rutProveedor,
      folio: folio,
      cuenta_id: null,
      centro_id: null,
      clasificador_id: null
    };
  }
},

  /**
   * Crear una asociación contable
   * @param {Object} asociacion - Datos de la asociación
   * @returns {Promise<Object>} - Promesa con la asociación creada
   */
  async crearAsociacion(asociacion) {
    return apiClient.post('/rcv/asociaciones', asociacion);
  },

  /**
  * Actualizar una asociación contable existente
  * @param {number} id - ID de la asociación a actualizar
  * @param {Object} asociacion - Datos actualizados de la asociación
  * @returns {Promise<Object>} - Promesa con la asociación actualizada
  */
  async actualizarAsociacion(id, asociacion) {
    return apiClient.put(`/rcv/asociaciones/${id}`, asociacion);
  },

  /**
 * Actualizar la asociación contable de un impuesto adicional
 * @param {number} impuestoId - ID del impuesto adicional
 * @param {Object} data - Datos de la asociación (cuenta_id, centro_id, clasificador_id)
 * @returns {Promise<Object>} - Promesa con la asociación actualizada
 */
  async actualizarAsociacionImpuesto(impuestoId, data) {
    return apiClient.put(`/rcv/impuestos/${impuestoId}/asociacion`, data);
  },

  /**
   * Exportar asociaciones a CSV
   * @param {Object} params - Parámetros de la exportación
   * @returns {Promise<Object>} - Promesa con información de la exportación
   */
  async exportarAsociaciones(params) {
    return apiClient.post('/rcv/asociaciones/exportar', params);
  },

  /**
   * Listar exportaciones previas
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo en formato YYYYMM
   * @returns {Promise<Array>} - Lista de exportaciones
   */
  async listarExportaciones(rutEmpresa, periodo) {
    return apiClient.get(`/rcv/asociaciones/exportaciones?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
  },

  /**
   * Obtener detalles de una exportación
   * @param {number} exportacionId - ID de la exportación
   * @returns {Promise<Object>} - Detalles de la exportación
   */
  async obtenerExportacion(exportacionId) {
    return apiClient.get(`/rcv/asociaciones/exportaciones/${exportacionId}`);
  },

  /**
   * Obtener información de asociaciones pendientes de exportar
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo en formato YYYYMM
   * @returns {Promise<Object>} - Información sobre pendientes
   */
  async obtenerPendientesExportar(rutEmpresa, periodo) {
    return apiClient.get(`/rcv/asociaciones/pendientes-exportar?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
  },
};

